import { useEffect, useState } from 'react';
import { Image, ImageSourcePropType, Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { BrandHeader } from '../components/AppHeader';
import BookCard from '../components/BookCard';
import BottomTabBar from '../components/BottomTabBar';
import colors from '../theme';
import { RootStackParamList } from '../navigation/RootNavigator';
import { Audiobook, getSuggestions, searchAudiobooks } from '../api/audiobooks';
import { narratorName } from '../utils/audiobookPeople';

type Props = NativeStackScreenProps<RootStackParamList, 'GetSuggestions'>;

const fallbackCover = require('../../assets/aural-book-1.png');

function imageSource(url: string | null | undefined): ImageSourcePropType {
  return url ? { uri: url } : fallbackCover;
}

function bookAuthor(book: Audiobook) {
  return narratorName(book);
}

function SearchSkeleton() {
  return (
    <View style={styles.skeletonList}>
      {[0, 1, 2].map((item) => (
        <View key={item} style={styles.skeletonRow}>
          <View style={styles.skeletonCover} />
          <View style={styles.skeletonTextBlock}>
            <View style={styles.skeletonTitle} />
            <View style={styles.skeletonAuthor} />
          </View>
        </View>
      ))}
    </View>
  );
}

export default function GetSuggestionsScreen({ navigation }: Props) {
  const [suggestions, setSuggestions] = useState<Audiobook[]>([]);
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Audiobook[]>([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const trimmedQuery = query.trim();

  useEffect(() => {
    let mounted = true;
    getSuggestions({ per_page: 10 })
      .then((items) => mounted && setSuggestions(items))
      .catch(() => mounted && setSuggestions([]));
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    if (!trimmedQuery) {
      setSearchResults([]);
      setSearching(false);
      setSearched(false);
      return () => {
        mounted = false;
      };
    }

    setSearching(true);
    const timer = setTimeout(() => {
      searchAudiobooks(trimmedQuery, 12)
        .then((items) => {
          if (mounted) {
            setSearchResults(items);
            setSearched(true);
          }
        })
        .catch(() => {
          if (mounted) {
            setSearchResults([]);
            setSearched(true);
          }
        })
        .finally(() => {
          if (mounted) {
            setSearching(false);
          }
        });
    }, 350);

    return () => {
      mounted = false;
      clearTimeout(timer);
    };
  }, [trimmedQuery]);

  const openBook = (book: Audiobook) => {
    navigation.navigate('BookOverview', { id: book.id, slug: book.slug });
  };

  return (
    <View style={styles.screen}>
      <ScrollView contentContainerStyle={styles.container} showsVerticalScrollIndicator={false}>
        <BrandHeader />

        <View style={styles.searchBox}>
          <Ionicons name="search-outline" size={20} color={colors.textSecondary} />
          <TextInput
            value={query}
            onChangeText={setQuery}
            placeholder="Tìm sách nói, câu chuyện, tác giả..."
            placeholderTextColor={colors.textSecondary}
            style={styles.searchInput}
            autoCapitalize="none"
            returnKeyType="search"
          />
          {query ? (
            <Pressable onPress={() => setQuery('')} hitSlop={8}>
              <Ionicons name="close-circle" size={20} color={colors.textSecondary} />
            </Pressable>
          ) : null}
        </View>

        {trimmedQuery ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Kết quả tìm kiếm</Text>
            {searching ? (
              <SearchSkeleton />
            ) : searchResults.length ? (
              <View style={styles.resultList}>
                {searchResults.map((book) => (
                  <Pressable key={book.id} style={styles.resultItem} onPress={() => openBook(book)}>
                    <Image source={imageSource(book.cover)} style={styles.resultCover} resizeMode="cover" />
                    <View style={styles.resultText}>
                      <Text style={styles.resultTitle} numberOfLines={2}>{book.name}</Text>
                      <Text style={styles.resultAuthor} numberOfLines={1}>{bookAuthor(book)}</Text>
                    </View>
                    <Ionicons name="chevron-forward" size={18} color={colors.textSecondary} />
                  </Pressable>
                ))}
              </View>
            ) : searched ? (
              <View style={styles.emptyState}>
                <Text style={styles.emptyTitle}>Không tìm thấy sách nói</Text>
                <Text style={styles.emptyText}>Thử nhập tên truyện, tác giả hoặc từ khóa khác.</Text>
              </View>
            ) : null}
          </View>
        ) : (
          <>
            <View style={styles.heroCard}>
              <Text style={styles.heroTitle}>Gợi ý cho em</Text>
              <Text style={styles.heroText}>
                Chọn nhanh những câu chuyện phù hợp theo sở thích và chủ đề đã quan tâm.
              </Text>
              <Pressable style={styles.ctaButton} onPress={() => navigation.navigate('Home')}>
                <Ionicons name="star" size={12} color={colors.buttonText} />
                <Text style={styles.ctaText}>Làm mới gợi ý</Text>
              </Pressable>
              <Pressable style={styles.secondaryButton} onPress={() => navigation.navigate('PreferencesGenre')}>
                <Ionicons name="color-wand-outline" size={12} color={colors.ink} />
                <Text style={styles.secondaryText}>Chọn sở thích</Text>
              </Pressable>
            </View>

            {suggestions.length ? (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Có thể em thích</Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                  {suggestions.map((book) => (
                    <BookCard
                      key={book.id}
                      title={book.name}
                      author={bookAuthor(book)}
                      imageSource={imageSource(book.cover)}
                      onPress={() => openBook(book)}
                    />
                  ))}
                </ScrollView>
              </View>
            ) : null}
          </>
        )}
      </ScrollView>
      <BottomTabBar
        active="Search"
        onHome={() => navigation.navigate('Home')}
        onDiscover={() => null}
        onLibrary={() => navigation.navigate('Library')}
        onProfile={() => navigation.navigate('Settings')}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: 'rgb(242, 249, 253)',
  },
  container: {
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 124,
  },
  searchBox: {
    height: 48,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    gap: 10,
    marginBottom: 24,
  },
  searchInput: {
    flex: 1,
    color: colors.textPrimary,
    fontSize: 14,
    paddingVertical: 0,
  },
  heroCard: {
    alignItems: 'center',
    paddingTop: 12,
  },
  heroTitle: {
    color: colors.textPrimary,
    fontSize: 14,
    fontWeight: '700',
    marginBottom: 8,
    textAlign: 'center',
  },
  heroText: {
    color: colors.textSecondary,
    fontSize: 14,
    lineHeight: 16,
    textAlign: 'center',
    marginBottom: 24,
    maxWidth: 250,
  },
  ctaButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.button,
    borderRadius: 5,
    paddingVertical: 10,
    paddingHorizontal: 20,
    gap: 7,
    marginBottom: 12,
  },
  ctaText: {
    color: colors.buttonText,
    fontWeight: '700',
    textAlign: 'center',
    fontSize: 14,
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 5,
    paddingVertical: 10,
    paddingHorizontal: 22,
    backgroundColor: colors.card,
    gap: 7,
  },
  secondaryText: {
    color: colors.ink,
    fontWeight: '700',
    fontSize: 14,
  },
  section: {
    marginTop: 4,
  },
  sectionTitle: {
    color: colors.textPrimary,
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 16,
  },
  resultList: {
    gap: 14,
  },
  resultItem: {
    minHeight: 76,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  resultCover: {
    width: 64,
    height: 64,
    borderRadius: 8,
    backgroundColor: colors.card,
  },
  resultText: {
    flex: 1,
  },
  resultTitle: {
    color: colors.textPrimary,
    fontSize: 15,
    fontWeight: '600',
    lineHeight: 20,
    marginBottom: 6,
  },
  resultAuthor: {
    color: colors.textSecondary,
    fontSize: 12,
    fontWeight: '500',
  },
  emptyState: {
    paddingVertical: 36,
    alignItems: 'center',
  },
  emptyTitle: {
    color: colors.textPrimary,
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 8,
  },
  emptyText: {
    color: colors.textSecondary,
    fontSize: 13,
    textAlign: 'center',
  },
  skeletonList: {
    gap: 14,
  },
  skeletonRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  skeletonCover: {
    width: 64,
    height: 64,
    borderRadius: 8,
    backgroundColor: '#ECEEF2',
  },
  skeletonTextBlock: {
    flex: 1,
  },
  skeletonTitle: {
    width: '82%',
    height: 16,
    borderRadius: 6,
    backgroundColor: '#ECEEF2',
    marginBottom: 10,
  },
  skeletonAuthor: {
    width: '48%',
    height: 12,
    borderRadius: 6,
    backgroundColor: '#ECEEF2',
  },
});
